/**
 * Bâtiments de la station (chalets, restaurant, location, caisses, zone
 * d'accueil, enneigeurs, lampadaires) : un InstancedLodSet par type. Un
 * chantier s'affiche à hauteur réduite selon son avancement.
 */
import * as THREE from 'three/webgpu';
import { type Building, type BuildingKind, type Heightfield, type Piste } from '@snowchain/shared';
import { BUILDINGS } from '@snowchain/shared';
import { InstancedLodSet, composeYawRoll, type Frustum } from './instancing';
import type { AssetLibrary } from './assets';
import { seatHeight } from './earthworks';
import { LOD_DISTANCES } from './lod';

interface Placed {
  kind: BuildingKind;
  x: number;
  y: number;
  z: number;
  r: number;
  m: Float32Array;
}

const CATEGORY: Record<BuildingKind, string> = {
  chalet_accueil: 'building',
  restaurant: 'building',
  shop_rental: 'building',
  ticket_office: 'building',
  welcome_area: 'building',
  snow_cannon: 'prop',
  lamp_post: 'prop',
  hotel: 'building',
  residence: 'building',
  ski_school: 'building',
  first_aid: 'building',
  snack_bar: 'building',
  groomer_garage: 'building',
  parking: 'building',
  reservoir: 'building',
};

/** Instances réservées au départ par type (le jeu agrandit au besoin). */
const CAPACITY: Partial<Record<BuildingKind, number>> = { lamp_post: 256, snow_cannon: 256, hotel: 16, residence: 16, groomer_garage: 8, reservoir: 8, parking: 16 };

export class BuildingsLayer {
  readonly group = new THREE.Group();
  private sets = new Map<BuildingKind, InstancedLodSet>();
  private placed: Placed[] = [];

  constructor(
    lib: AssetLibrary,
    hf: Heightfield,
    /** Hauteur d'assise (plateforme des terrassements) ; par défaut la pente du relief. */
    private readonly seat: (b: Building) => number = (b) => seatHeight(hf, b),
  ) {
    this.group.name = 'buildings';
    for (const kind of Object.keys(BUILDINGS) as BuildingKind[]) {
      const asset = BUILDINGS[kind].asset;
      if (!lib.has(asset)) continue;
      const cat = CATEGORY[kind];
      this.sets.set(kind, new InstancedLodSet(kind, this.group, lib.get(asset).geometries, lib.instanced, CAPACITY[kind] ?? 64, LOD_DISTANCES[cat] as number[], { shadowLods: 1 }));
    }
    this.radius = (k) => lib.get(BUILDINGS[k].asset).bounds.radius;
  }

  private radius: (k: BuildingKind) => number;

  /** Reconstruit la liste des instances (changement de bâtiments). */
  rebuild(buildings: Iterable<Building>, pistes: Map<number, Piste>): void {
    this.placed = [];
    for (const b of buildings) {
      const y = this.seat(b);
      let yaw = b.rot;
      if (b.kind === 'snow_cannon' && b.pisteId !== null) {
        // L'enneigeur souffle vers sa piste.
        const p = pistes.get(b.pisteId);
        if (p) yaw = aimAt(b.x, b.z, p);
      }
      const grow = b.status === 'construction' ? 0.15 + 0.85 * b.progress : 1;
      const m = new Float32Array(16);
      composeYawRoll(m, 0, b.x, y, b.z, yaw, 0, 1, grow, 1);
      this.placed.push({ kind: b.kind, x: b.x, y, z: b.z, r: this.radius(b.kind), m });
    }
  }

  update(camera: THREE.Camera, frustum: Frustum): void {
    const cp = camera.position;
    for (const s of this.sets.values()) s.begin();
    for (const p of this.placed) {
      if (!frustum.sphere(p.x, p.y + p.r * 0.4, p.z, p.r)) continue;
      const set = this.sets.get(p.kind);
      if (!set) continue;
      set.add(p.m, 0, Math.hypot(p.x - cp.x, p.y - cp.y, p.z - cp.z));
    }
    for (const s of this.sets.values()) s.end();
  }

  stats(): { instances: number; triangles: number } {
    let instances = 0;
    let triangles = 0;
    for (const s of this.sets.values()) {
      instances += s.instanceCount;
      triangles += s.triangleCount;
    }
    return { instances, triangles };
  }
}

function aimAt(x: number, z: number, p: Piste): number {
  let best = Infinity;
  let tx = x;
  let tz = z;
  for (const q of p.path) {
    const d = Math.hypot(q[0] - x, q[1] - z);
    if (d < best) {
      best = d;
      tx = q[0];
      tz = q[1];
    }
  }
  return Math.atan2(tx - x, tz - z);
}
